import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { useT } from "../i18n/LanguageContext";
import BookCard from "../components/BookCard";
import WaitlistForm from "../components/WaitlistForm";

export default function Curriculum() {
  const t = useT();
  const root = useReveal();
  const volumes = t("curriculum.volumes");

  const VOL_PROPS = [
    { image: "/images/cover_body.png", word: "BODY", slug: "body" },
    { word: "MIND", slug: "mind" },
    { word: "SPIRIT", slug: "spirit" },
  ];

  return (
    <div ref={root} className="pt-32 md:pt-40">
      <section className="container-x py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="reveal eyebrow">{t("curriculum.eyebrow")}</p>
            <h1 className="reveal reveal-delay-1 mt-8 font-serif text-5xl md:text-7xl lg:text-[6.5rem] tracking-tight text-[#1A1A1A] leading-[0.95]">
              {t("curriculum.title")}
            </h1>
          </div>
          <div className="md:col-span-4 md:pb-3">
            <p className="reveal reveal-delay-2 text-lg text-[#4A4A4A] leading-relaxed">
              {t("curriculum.intro")}
            </p>
          </div>
        </div>
      </section>

      <section className="bg-[#F3F1EA] border-y border-[rgba(26,26,26,0.06)] py-24 md:py-32">
        <div className="container-x space-y-24 md:space-y-32">
          {volumes.map((v, i) => (
            <article
              key={i}
              className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start"
              data-testid={`curriculum-volume-${i + 1}`}
            >
              <div className="lg:col-span-5 reveal">
                <BookCard
                  num={v.num}
                  title={v.title}
                  sub={v.sub}
                  word={VOL_PROPS[i].word}
                  image={VOL_PROPS[i].image}
                  status={v.status}
                />
              </div>
              <div className="lg:col-span-7 reveal reveal-delay-1">
                <p className="text-[0.7rem] uppercase tracking-[0.32em] text-[#9A7E47]">
                  {v.num} · {v.sub}
                </p>
                <h2 className="mt-6 font-serif text-4xl md:text-5xl text-[#1A1A1A] tracking-tight leading-[1.02]">
                  {v.title}
                </h2>
                <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed max-w-2xl">
                  {v.body}
                </p>

                <div className="hairline mt-10" />

                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
                  {v.chapters.map((c, k) => (
                    <li
                      key={k}
                      className="flex items-start gap-3 text-[#2D2D2D] text-sm py-1"
                    >
                      <span className="mt-1 inline-flex h-5 w-5 items-center justify-center border border-[#B89B5E]/40 text-[#9A7E47]">
                        <Check size={10} />
                      </span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-10">
                  <Link
                    to={`/${VOL_PROPS[i].slug}`}
                    className="gold-link"
                    data-testid={`curriculum-volume-link-${i + 1}`}
                  >
                    Read the pillar <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="container-x py-32 md:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <p className="reveal eyebrow">Enrol</p>
            <h2 className="reveal reveal-delay-1 mt-6 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#1A1A1A] leading-[1.02]">
              {t("curriculum.cta.title")}
            </h2>
            <p className="reveal reveal-delay-2 mt-6 text-[#4A4A4A] max-w-md leading-relaxed">
              {t("curriculum.cta.sub")}
            </p>
          </div>
          <div className="lg:col-span-7">
            <div className="reveal">
              <WaitlistForm
                interest="curriculum"
                testIdPrefix="curriculum-waitlist"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
